import React, { useState } from 'react';
import { toAbsoluteUrl } from '../../../helpers';
import { toast } from 'react-toastify';
import { createOne } from '../../../../app/services/common_requests';
import { useQueryResponse } from '../../../../app/pages/hotel-management/hotel-list/core/QueryResponseProvider';
import { useIntl } from 'react-intl';
import { ImagePlacement, recommendedSizeFor } from '../../../../app/utils/imagePlacements';

interface ImageUploaderProps {
  onImageUpload: (image: File | null) => void;
  onImageAdded: (image: { url: string; _id: string }) => void;
  uploadEndpoint: string;
  hotelId?: string;
  tourId?: string;
  /** Placement preset → API WebP resize/crop */
  placement?: ImagePlacement | string;
  /** Override recommended size label (defaults from placement) */
  recommendedSize?: string;
  showSizeHint?: boolean;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({ 
  onImageUpload, 
  onImageAdded, 
  uploadEndpoint, 
  hotelId,
  tourId,
  placement = 'general',
  recommendedSize,
  showSizeHint = true,
}) => {
  const [image, setImage] = useState<string | null>(null);
  const {refetchData} = useQueryResponse()
  const intl=useIntl()
  const sizeLabel = recommendedSize || recommendedSizeFor(placement)

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        setImage(event.target?.result as string);
      };
      reader.readAsDataURL(file);
      const formData = new FormData();
      formData.append('image', file);
      formData.append('placement', String(placement));
      
      // Add either hotel or tour ID based on what's provided
      if (hotelId) {
        formData.append('hotel', hotelId);
        formData.append('type', 'hotel');
      }
      if (tourId) {
        formData.append('tour', tourId);
        formData.append('type', 'tour');
      }

      const endpointWithPlacement = uploadEndpoint.includes('?')
        ? `${uploadEndpoint}&placement=${encodeURIComponent(String(placement))}`
        : `${uploadEndpoint}?placement=${encodeURIComponent(String(placement))}`

      try {
        await createOne(formData, endpointWithPlacement).then((response) => {
          if (response?.payload?.code === 2021) {
            onImageUpload(file);
            onImageAdded({ url: response?.data.url, _id: response?.data._id });
            refetchData()
            toast.success(
              intl.formatMessage({
                id: 'ADMIN.COMMON.SUCCESS.IMAGE_UPDATED_SUCCESSFULLY',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            )
            refetchData()
          } else {
            toast.error(
              intl.formatMessage({
                id: 'ADMIN.COMMON.ERROR.IMAGE_UPDATED',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            )
          }
        })
      } catch (error) {
        toast.error(
          intl.formatMessage({
            id: 'ADMIN.COMMON.ERROR.IMAGE',
          }),
          {
            position: 'top-right',
            autoClose: 4000,
          }
        )
      }
    }
  };

  return (
    <div className='d-inline-flex flex-column align-items-start'>
      <div className='image-container-dashed'>
        <div className='upload-input-container'>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className='inpute'
          />
        </div>
       
        {image ? (
          <img src={image} alt="Preview" className='preview-image-uploader' />
        ) : (
          <div className='image-container-placeholder'>
            <img src={toAbsoluteUrl('/media/icons/upload-image.png')} alt="uploadimage"/>
            <span>{intl.formatMessage({
              id: 'COMMON.ADD',
            })}</span>
          </div>
        )}
      </div>
      {showSizeHint && sizeLabel ? (
        <div className='form-text text-muted mt-2 mb-0'>
          {intl.formatMessage(
            { id: 'COMMON.IMAGE.RECOMMENDED_SIZE' },
            { size: sizeLabel },
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ImageUploader;
